/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the depot management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useHistory } from 'react-router';

import { Theme, makeStyles } from '@material-ui/core/styles';

/**
 * Used to format text.
 */
import Typography from '@material-ui/core/Typography';

import { Button, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import * as Dtos from '../../api/Neon.Dtos';
import { useOrderForms } from '../../store/modules/drugmanagement/orderforms';
import { useDepots } from '../../store/modules/drugmanagement/depots';
import { useDepot } from '../../store/modules/drugmanagement/depot';
import DepotModal from './Modals/DepotModal';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import { CollapsibleTable } from '@ngt/online-patient-management-ui';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDepotManagementProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        paddingBottom: theme.spacing(1),
        textAlign: 'right'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const useDepotColumns = (depots: Dtos.Depot[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Depot>> = [
            {
                Header: 'Depot Name',
                accessor: 'depotName',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Address',
                accessor: 'address',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Email',
                accessor: 'email',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Contact Name',
                accessor: 'contactName',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Phone Number',
                accessor: 'phoneNo',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Status',
                accessor: (depot) =>depot.active ? 'Active' : 'Inactive',
                minWidth: 155,
                maxWidth: 155
            }
        ]

        return cols;
    }, []);

    return columns;
};

const DepotManagement: React.FunctionComponent<IDepotManagementProps> = () => {
    const classes = useStyles();

    const history = useHistory();

    const [orderForms, orderFormLoadState, orderFormActions] = useOrderForms();

    const [depots, depotsLoadState, depotsActions] = useDepots();

    const [, , , depotActions] = useDepot();

    const [modalOpen, setModalOpen] = React.useState(false);

    const [depotForm, setDepot] = React.useState<Dtos.Depot | null>(null);

    React.useEffect(() => {
        orderFormActions.load();

        return () => {
            orderFormActions.clear();
        }
    }, [orderFormActions]);

    React.useEffect(() => {
        depotsActions.load();

        return () => {
            depotsActions.clear();
        }
    }, [depotsActions]);

    const onFormSave = React.useCallback(() => {
        depotActions.clear();
        depotsActions.load();
    }, [depotActions, depotsActions]);

    const addDepotClick = React.useCallback(() => {
        setDepot(new Dtos.Depot({ orderFormId: 1 }));

        setModalOpen(true);
    }, [setModalOpen]);

    const onRowClick = React.useCallback((row: Row<Dtos.Depot>) => {
        if (row &&
            row.original) {
            history.push(`/drug-management/depot/${row.original.id}`);
        }
    }, [history]);

    const depotColumns = useDepotColumns(depots);

    return (
        <>
            <DepotModal
                depot={depotForm}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                orderForms={orderForms}
                orderFormLoadState={orderFormLoadState}
                onFormSave={onFormSave}
            />
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                        showInstitution={false}
                        showDepot={true}
                    />
                }
                heading="Depots"
            >
                <Paper
                    className={classes.container}
                >
                    <div
                        className={classes.buttonSet}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={addDepotClick}
                        >
                            Add Depot
                        </Button>
                    </div>
                    <CollapsibleTable
                        title="Depot"
                        loadState={depotsLoadState}
                        data={depots}
                        columns={depotColumns}
                        onRowClick={onRowClick}
                    />
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DepotManagement;